import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { apiClient } from '@/api/client'
import { Eraser, Loader2, Check, CheckCircle, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface OrderSignInfo {
  tenant_name: string
  order_category: string
  pickup_datetime: string | null
  flight_number: string
  pickup_location: string
  dropoff_location: string
  additional_stops: string[]
  passenger_name: string
  passenger_phone: string
  passenger_count: number
  luggage_count: number
  payment_method: string
  amount: number | null
  notes: string
}

const DRIVER_STORAGE_KEY = 'ext_sign_driver'

interface DriverForm {
  name: string
  phone: string
  plate: string
  carType: string
}

const loadDriver = (): DriverForm => {
  try {
    const raw = localStorage.getItem(DRIVER_STORAGE_KEY)
    if (raw) return { name: '', phone: '', plate: '', carType: '', ...JSON.parse(raw) }
  } catch { /* localStorage 不可用就當作沒記過 */ }
  return { name: '', phone: '', plate: '', carType: '' }
}

const formatDateTime = (iso: string | null) => {
  if (!iso) return '-'
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-3 py-1.5 text-sm">
      <span className="w-20 shrink-0 text-gray-500 dark:text-gray-400">{label}</span>
      <span className="flex-1 font-medium text-gray-900 dark:text-white break-words">{value || '-'}</span>
    </div>
  )
}

export default function ExternalSignPage() {
  const { token } = useParams<{ token: string }>()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasSignature, setHasSignature] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [info, setInfo] = useState<OrderSignInfo | null>(null)
  const [loadError, setLoadError] = useState('')
  const [error, setError] = useState('')
  const [signed, setSigned] = useState(false)
  const [driver, setDriver] = useState<DriverForm>(loadDriver)

  const load = useCallback(async () => {
    if (!token) return
    try {
      const res = await apiClient.get<OrderSignInfo>(`/public/order-sign/${token}`)
      setInfo(res.data)
    } catch (e: any) {
      setLoadError(e?.response?.data?.detail || '連結無效或已失效')
    } finally {
      setIsLoading(false)
    }
  }, [token])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (canvas && ctx) {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.strokeStyle = '#000000'
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
    }
  }, [info])

  const updateDriver = (patch: Partial<DriverForm>) => {
    const next = { ...driver, ...patch }
    setDriver(next)
    try { localStorage.setItem(DRIVER_STORAGE_KEY, JSON.stringify(next)) } catch { /* ignore */ }
  }

  const getPos = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect()
    const sx = canvas.width / rect.width
    const sy = canvas.height / rect.height
    if ('touches' in e) return { x: (e.touches[0].clientX - rect.left) * sx, y: (e.touches[0].clientY - rect.top) * sy }
    return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy }
  }

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    setIsDrawing(true)
    const { x, y } = getPos(e, canvas)
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    const { x, y } = getPos(e, canvas)
    ctx.lineTo(x, y)
    ctx.stroke()
    setHasSignature(true)
  }

  const clearSignature = () => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    setHasSignature(false)
  }

  const driverComplete = !!(driver.name.trim() && driver.phone.trim() && driver.plate.trim())

  const submit = async () => {
    if (!canvasRef.current || !hasSignature || !driverComplete || !token) return
    setIsSaving(true)
    setError('')
    try {
      const blob = await (await fetch(canvasRef.current.toDataURL('image/png'))).blob()
      const form = new FormData()
      form.append('file', blob, 'signature.png')
      form.append('driver_name', driver.name.trim())
      form.append('driver_phone', driver.phone.trim())
      form.append('vehicle_number', driver.plate.trim())
      form.append('vehicle_type', driver.carType.trim())
      await apiClient.post(`/public/order-sign/${token}/sign`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setSigned(true)
    } catch (e: any) {
      setError(e?.response?.data?.detail || '簽名送出失敗，請稍後再試')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (loadError || !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center space-y-2">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
          <p className="text-gray-700 dark:text-gray-300">{loadError || '連結無效或已失效'}</p>
        </div>
      </div>
    )
  }

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center space-y-3">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
          <p className="text-lg font-bold text-gray-900 dark:text-white">簽名已送出，謝謝！</p>
          <p className="text-sm text-gray-500">此連結已完成，無法再次使用</p>
        </div>
      </div>
    )
  }

  const route = [info.pickup_location, ...info.additional_stops, info.dropoff_location]

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-3 flex flex-col gap-3 max-w-lg mx-auto">
      <p className="text-center text-sm text-gray-500 dark:text-gray-400">{info.tenant_name} 派車簽單</p>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <h2 className="text-sm font-bold text-gray-900 dark:text-white">司機資料（請填寫）</h2>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="姓名 *" value={driver.name} onChange={e => updateDriver({ name: e.target.value })} />
          <Input placeholder="電話 *" inputMode="tel" value={driver.phone} onChange={e => updateDriver({ phone: e.target.value })} />
          <Input placeholder="車號 *" value={driver.plate} onChange={e => updateDriver({ plate: e.target.value })} />
          <Input placeholder="車型" value={driver.carType} onChange={e => updateDriver({ carType: e.target.value })} />
        </div>
        <p className="text-xs text-gray-400">資料只會存在這支手機，下次開啟自動帶入。</p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 divide-y divide-gray-100 dark:divide-gray-700">
        <Row label="類別" value={info.order_category} />
        <Row label="用車時間" value={formatDateTime(info.pickup_datetime)} />
        {info.flight_number && <Row label="航班" value={info.flight_number} />}
        <Row label="上車地點" value={route[0]} />
        {info.additional_stops.map((s, i) => <Row key={i} label={`中途點 ${i + 1}`} value={s} />)}
        <Row label="下車地點" value={route[route.length - 1]} />
        <Row label="乘客姓名" value={info.passenger_name} />
        <Row label="聯絡電話" value={info.passenger_phone && <a href={`tel:${info.passenger_phone}`} className="text-blue-600">{info.passenger_phone}</a>} />
        <Row label="人數/行李" value={`${info.passenger_count} 人 / ${info.luggage_count} 件`} />
        <Row label="付款方式" value={info.payment_method} />
        {info.amount !== null && <Row label="金額" value={`$${info.amount.toLocaleString()}`} />}
        {info.notes && <Row label="備註" value={<span className="whitespace-pre-wrap">{info.notes}</span>} />}
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-400">請客戶在下方簽名：</p>
      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={500}
          height={250}
          className="w-full touch-none cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={() => setIsDrawing(false)}
          onMouseLeave={() => setIsDrawing(false)}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={() => setIsDrawing(false)}
        />
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}
      {!driverComplete && <p className="text-xs text-amber-600">請先填寫司機姓名、電話、車號才能送出。</p>}

      <div className="flex justify-between gap-3 pb-4">
        <Button variant="outline" onClick={clearSignature}>
          <Eraser className="w-4 h-4 mr-2" />清除
        </Button>
        <Button onClick={submit} disabled={!hasSignature || !driverComplete || isSaving}>
          {isSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />送出中...</> : <><Check className="w-4 h-4 mr-2" />送出簽名</>}
        </Button>
      </div>
    </div>
  )
}
